import { useState, useCallback } from 'react'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'

export interface CodeFrameItem {
  type: 'code' | 'net' | 'arith'
  key: string      // code number, net syntax, or arith syntax
  label: string
  syntax?: string  // constructor syntax (for computed variables)
}

export interface FrameMetadata {
  name: string
  description?: string
  items: CodeFrameItem[]
  noSort?: string[]
}

export interface ConstructorMetadata {
  flags: string
  lines: Array<{
    /** Code tag as Carbon writes it: "53", hierarchic "1:2", or "*" (uncoded). */
    codeTag: string
    /** The whole rule: filter, then an optional space and increment. */
    expression: string
    codes: number[]
    filter: string
    increment: string
    factor: number
    codeRange: string
  }>
}

export interface VariableMetadata {
  name: string
  description: string
  cases: number
  dataType?: string
  frames: FrameMetadata[]
  constructorDef?: ConstructorMetadata
  special?: Record<string, string>
}

// Legacy type kept for backward compat with openEditor signature
export interface VariableCode {
  code: string
  label: string
}

interface UpdateVariableRequest {
  description?: string
  codes?: Record<string, string>
  addCodes?: Record<string, string>
  removeCodes?: string[]
  special?: Record<string, string>
}

interface UpdateVariableResponse {
  message: string
  variable: string
  changes: string[]
}

export const useVariableEditor = ({
  customer,
  job,
}: {
  customer: string
  job: string
}) => {
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isConstructing, setIsConstructing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveResult, setSaveResult] = useState<string[] | null>(null)
  const [metadata, setMetadata] = useState<VariableMetadata | null>(null)

  // Open the editor — always loads full metadata from the MET file
  const openEditor = useCallback(async (variableName: string) => {
    setIsOpen(true)
    setError(null)
    setSaveResult(null)
    setIsLoading(true)

    try {
      const res = await axios.get<VariableMetadata>(
        `${API_BASE_URL}/job/${customer}/${job}/variables/${encodeURIComponent(variableName)}/metadata`
      )
      setMetadata(res.data)
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message)
      setMetadata({
        name: variableName,
        description: '',
        cases: 0,
        frames: [],
      })
    } finally {
      setIsLoading(false)
    }
  }, [customer, job])

  const closeEditor = useCallback(() => {
    setIsOpen(false)
    setMetadata(null)
    setError(null)
    setSaveResult(null)
  }, [])

  // Save changes to the variable
  const saveVariable = useCallback(async (updates: UpdateVariableRequest) => {
    if (!metadata) return
    setIsSaving(true)
    setError(null)
    setSaveResult(null)
    try {
      const res = await axios.put<UpdateVariableResponse>(
        `${API_BASE_URL}/job/${customer}/${job}/variables/${encodeURIComponent(metadata.name)}`,
        updates
      )
      setSaveResult(res.data.changes)
      queryClient.invalidateQueries({ queryKey: ['vartree'] })
      return res.data
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }, [customer, job, metadata, queryClient])

  // Construct (rebuild .cd data) after editing
  const constructVariable = useCallback(async () => {
    if (!metadata) return
    setIsConstructing(true)
    setError(null)
    try {
      const res = await axios.post<{ constructedCount: number; variables: string[]; errors: string[] }>(
        `${API_BASE_URL}/job/${customer}/${job}/construct`,
        { variables: [metadata.name] }
      )
      if (res.data.errors && res.data.errors.length > 0) {
        setError(`Construct errors: ${res.data.errors.join(', ')}`)
      } else {
        setSaveResult(prev => [...(prev || []), `${metadata.name} reconstructed`])
      }
      queryClient.invalidateQueries({ queryKey: ['vartree'] })
      return res.data
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message)
      throw err
    } finally {
      setIsConstructing(false)
    }
  }, [customer, job, metadata, queryClient])

  return {
    isOpen,
    isLoading,
    isSaving,
    isConstructing,
    error,
    saveResult,
    metadata,
    openEditor,
    closeEditor,
    saveVariable,
    constructVariable,
  }
}

export default useVariableEditor
